import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { FormFieldDefinition } from '../../types/form.types'
import useRecordForm from '../../hooks/useRecordForm'
import FieldRenderer from '../FieldRenderer/FieldRenderer'
import {
  FormShell,
  FormGrid,
  FormField,
  FormFooter,
} from '../../../ui/FormLayout'
import { AccentButton } from '../../../ui/Button/AccentButton'

type Props<T> = {
  fields: FormFieldDefinition<T>[]
  initialValues?: Partial<T>
  onSubmit: (values: Partial<T>) => void
  onDelete?: () => void
  submitLabel?: string
  isLoading?: boolean
  isDeleting?: boolean
}

const RecordForm = <T,>({
  fields,
  initialValues,
  onSubmit,
  onDelete,
  submitLabel = 'שמור',
  isLoading,
  isDeleting,
}: Props<T>) => {
  const { values, errors, handleChange, handleReset, validate } =
    useRecordForm<T>(fields, initialValues)

  useEffect(() => {
    handleReset(initialValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues])

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    if (!validate()) return
    onSubmit(values)
  }

  const regularFields = useMemo(
    () => fields.filter((f) => f.inputType !== 'boolean-select' && f.inputType !== 'checkbox'),
    [fields]
  )
  const booleanFields = useMemo(
    () => fields.filter((f) => f.inputType === 'boolean-select' || f.inputType === 'checkbox'),
    [fields]
  )

  return (
    <Form onSubmit={handleFormSubmit} noValidate>
      <FormShell>
        <FormGrid>
          {regularFields.map((field) => (
            <FormField
              key={String(field.name)}
              label={field.label}
              required={field.required}
              fullWidth={field.inputType === 'textarea'}
            >
              <FieldRenderer
                field={field}
                value={values[field.name]}
                error={errors[field.name]}
                onChange={handleChange}
                disabled={isLoading}
              />
            </FormField>
          ))}
        </FormGrid>

        {booleanFields.length > 0 && (
          <BooleanGrid>
            {booleanFields.map((field) => (
              <FieldRenderer
                key={String(field.name)}
                field={field}
                value={values[field.name]}
                error={errors[field.name]}
                onChange={handleChange}
                disabled={isLoading}
              />
            ))}
          </BooleanGrid>
        )}

        <Footer>
          <AccentButton type="submit" disabled={isLoading || isDeleting} fullWidth={false}>
            {isLoading ? 'שומר...' : submitLabel}
          </AccentButton>
          {onDelete && (
            <DeleteButton
              type="button"
              variant="danger"
              fullWidth={false}
              onClick={onDelete}
              disabled={isLoading || isDeleting}
            >
              מחק
            </DeleteButton>
          )}
        </Footer>
      </FormShell>
    </Form>
  )
}

export default RecordForm

const Form = styled.form`
  width: 100%;
`

const Footer = styled(FormFooter)`
  gap: 0.5em;
`

// Pushed to the opposite (far) end of the footer, away from save/cancel.
const DeleteButton = styled(AccentButton)`
  margin-inline-start: auto;
`

const BooleanGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.line};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 0.625rem 0.5rem;
  }
`
